import * as React from 'react';
import get from 'lodash/get';
import { ValidationProps } from 'ui';
import { ExtraFooterButton } from 'ui/common/select/types';
import { ComplexValue } from 'app/types-shared';
import { OptionsProps } from 'app/components/options-handler';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { SimpleFormComponentProps } from 'app/infrastructure/simple-form/types';
import { addToArrayIfNotAlreadyThere, groupSelectorFieldName, addSelectedValues, updateSelectedValues } from './options-panel.utils';
import { GroupingType, ClearValidationAction } from '../types';
import { withRoleBasedAccess, RoleBasedAccessCustomStateProps } from '../role-based-access';
import { OptionPanel } from './option-panel';
import { OptionCard } from './option-card';

import styles from './options-panel.scss';
import { copyWithoutProperties } from 'app/infrastructure/data/object';
import { sortByDescriptionAscending } from 'app/infrastructure/data/sort';

export interface OptionsPanelExternalProps extends OptionsProps {
  readOnly: boolean;
  translation: any;
  initialValue: any;

  formComponent: React.ComponentType<SimpleFormComponentProps>;

  formValues: any;
  formName: string;
  groupingName: string;
  groupingType?: GroupingType;
  valueName: string;

  groupingOptionsEndpoint: string;
  valuesOptionsEndpoint: (id: number) => string;
  useLabelOption: boolean;
  singleStepAccess: boolean;
  validation: { [key: string]: ValidationProps };
  groupingLabel: string;
  valueLabel?: string;
  addButtonText: string;
  addSubButtonText?: string;
  displaySelectAll?: boolean;
  selectedGroup?: ComplexValue<number>;
  levelsNotAllowed?: boolean;
  clearValueInStore: (id: string) => void;
  clearValidation: ClearValidationAction;
  extraFooterButton?: ExtraFooterButton;
}

export interface OptionsPanelState {
  selectedOptions: ComplexValue<number>[];
  selectedValues: { [key: number]: ComplexValue<number>[] };
  initialValues?: { [key: number]: ComplexValue<number>[] };
}

type OptionsPanelComponentProps = OptionsPanelExternalProps & RoleBasedAccessCustomStateProps<OptionsPanelState>;

class OptionsPanelComponent extends React.Component<OptionsPanelComponentProps> {

  public static defaultProps: Partial<OptionsPanelComponentProps> = {
    groupingType: GroupingType.Single
  };

  public componentDidMount() {
    this.buildInitalState();
  }

  public componentDidUpdate() {
    this.buildInitalState();
  }

  private buildInitalState = () => {
    // Check if state does not exist and there is initial value
    if (this.props.customState || !this.props.initialValue) {
      return;
    }

    const { initialValue, groupingName, valueName, changeCustomState } = this.props;
    const initialGroups: ComplexValue<number>[] = (initialValue && initialValue.map(v => ({
      value: v[groupingName].v,
      description: v[groupingName].d
    }))) || [];

    const selectedOptions = initialGroups.reduce(addToArrayIfNotAlreadyThere, []);
    const selectedValues = updateSelectedValues(initialValue, groupingName, valueName);

    changeCustomState({
      selectedOptions,
      selectedValues,
      initialValues: selectedValues
    });
  };

  private removeGroup = (groupValue: number) => {

    const { customState = { selectedValues: {}, selectedOptions: [] }, changeCustomState } = this.props;
    const newSelectedOptions = customState.selectedOptions.filter(o => o.value !== groupValue);

    const newSelectedValues = copyWithoutProperties(customState.selectedValues, [String(groupValue)]);

    this.clearValidation(groupValue);

    changeCustomState({
      ...customState,
      selectedOptions: [
        ...newSelectedOptions
      ],
      selectedValues: {
        ...newSelectedValues
      }
    });
  };

  private removeAll = () => {
    this.props.changeCustomState({
      ...this.props.customState,
      selectedOptions: [],
      selectedValues: {}
    });
  };

  private onChange = (values: ComplexValue<number>[], groupValue: number) => {

    const { useLabelOption, customState = { selectedValues: {}, selectedOptions: [] }, changeCustomState } = this.props;

    if (values.length === 0 || !useLabelOption) {
      this.clearValidation(groupValue);
    } else {

      const removedValues = (customState.selectedValues[groupValue] || [])
        .filter(itemValue => !values.some(value => value.value === itemValue.value));

      removedValues.forEach(itemValue => this.clearValidation(groupValue, itemValue.value));
    }

    changeCustomState({
      ...customState,
      selectedOptions: customState.selectedOptions,
      selectedValues: {
        ...customState.selectedValues,
        [groupValue]: values.length === 0
          ? undefined
          : [...values]
      }
    });
  };

  private addGroup = () => {
    const { formValues, formName, groupingName, valueName, useLabelOption, clearValueInStore,
      customState = { selectedValues: {}, selectedOptions: [] }, changeCustomState } = this.props;
    const groupSelectorName = groupSelectorFieldName(formName, groupingName);
    const valueSelectorName = groupSelectorFieldName(formName, valueName);

    const selectedGroup = get(formValues, groupSelectorName);
    const selectedValues = get(formValues, valueSelectorName);

    if (!selectedGroup) {
      return;
    }

    let newSelectedOptions: ComplexValue<number>[] = customState.selectedOptions;
    let newSelectedValues = customState.selectedValues;

    if (this.props.groupingType === GroupingType.Single) {

      if (!selectedValues || selectedValues.length === 0) {
        return;
      }

      newSelectedOptions = addToArrayIfNotAlreadyThere(newSelectedOptions, selectedGroup);
      newSelectedValues = useLabelOption
        ? addSelectedValues(customState.selectedValues, selectedGroup.value, selectedValues)
        : customState.selectedValues;

    } else {

      newSelectedOptions = selectedGroup.reduce(addToArrayIfNotAlreadyThere, newSelectedOptions);
    }

    clearValueInStore(useLabelOption ? valueSelectorName : groupSelectorName);

    changeCustomState({
      ...customState,
      selectedOptions: newSelectedOptions,
      selectedValues: newSelectedValues
    });
  };

  private clearValidation = (groupValue: number, itemValue?: number) => {
    const { formName, groupingName, valueName, clearValidation } = this.props;
    clearValidation(formName, {
      [groupingName]: groupValue,
      ...(isDefinedIncludingZero(itemValue) && { [valueName]: itemValue })
    });
  };

  public render() {

    const { validation = {}, translation, formName, groupingName, valueName,
      useLabelOption, readOnly, singleStepAccess, selectedGroup,
      addButtonText, addSubButtonText, valuesOptionsEndpoint, options, loadOptions } = this.props;

    const { selectedOptions = [], selectedValues = {}, initialValues = {} } = this.props.customState || {};

    const accessCards = <>
      <div className="row">
        {sortByDescriptionAscending(selectedOptions).map(group => <OptionCard
          key={`${formName}-${group.value}`}
          groupId={group.value}
          label={group.description}
          valueItems={selectedValues[group.value]}
          initialValueItems={initialValues[group.value]}
          options={options}
          loadOptions={loadOptions}
          formName={formName}
          groupingName={groupingName}
          valueName={valueName}
          useLabelOption={useLabelOption}
          readOnly={readOnly}
          validation={validation}
          translation={translation}
          valuesOptionsEndpoint={valuesOptionsEndpoint}
          addGroup={this.addGroup}
          removeGroup={this.removeGroup}
          onChange={this.onChange}>
          {!singleStepAccess && !readOnly
            && <OptionPanel
              {...this.props}
              group={group}
              useStaticOptions={useLabelOption}
              addGroup={this.addGroup}
              addButtonText={addSubButtonText}
            />}
        </OptionCard>)}
      </div>
    </>;

    return <div className={`${styles.optionsPanel} ${!selectedOptions.length && !validation[formName]
      ? styles.addButtonMargin : undefined}`}>
      {!readOnly && <OptionPanel
        {...this.props}
        group={selectedGroup}
        isOptionValues={true}
        useStaticOptions={useLabelOption}
        isDoubleStep={!singleStepAccess}
        addGroup={this.addGroup}
        addButtonText={addButtonText}
        removeAll={this.removeAll}
      />}
      {selectedOptions.length || validation[formName]
        ? <div className={styles.content}>{accessCards}</div>
        : readOnly && <p className="no-content">{translation.no_access_has_been_granted}</p>}
    </div>;
  }
}

export const OptionsPanel = withRoleBasedAccess<OptionsPanelExternalProps, OptionsPanelState>(OptionsPanelComponent);
